import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from '../translations';
import SafeIcon from '../components/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

const { FiMail, FiArrowLeft, FiCheckCircle, FiAlertCircle, FiShield } = FiIcons;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const { t } = useTranslation();

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email) {
      setError(t('auth.error.emptyEmail'));
      setLoading(false);
      return;
    }

    try {
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const currentOrigin = window.location.origin;
      
      let redirectUrl = currentOrigin;
      if (isDevelopment) {
        redirectUrl = `${window.location.protocol}//${window.location.hostname}:${window.location.port}`;
      }
      
      const resetLink = `${redirectUrl}/reset-password`;
      
      // First, initiate Supabase password reset (required for the token)
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: resetLink,
      });

      if (error) throw error;

      // Then send our custom styled email via edge function
      await supabase.functions.invoke('send-password-reset', {
        body: { 
          email, 
          resetLink,
          userName: email.split('@')[0]
        }
      });

      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError(t('auth.error.resetPasswordFailed'));
    } finally {
      setLoading(false);
    }
  };

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    in: { opacity: 1, y: 0 },
    out: { opacity: 0, y: -20 },
  };

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={{ duration: 0.4 }}
      className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white flex items-center justify-center p-4 relative overflow-hidden"
    >
      {/* Background decorative elements - matching landing page */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 -right-20 w-96 h-96 bg-cyan-600/10 rounded-full blur-3xl"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="relative z-10 w-full max-w-md p-8 space-y-6 bg-white/5 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10"
      >
        {/* Header */}
        <div className="text-center space-y-4">
          {/* Logo */}
          <div className="flex justify-center mb-2">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 to-cyan-600/30 rounded-xl blur-lg opacity-50"></div>
              <div className="relative p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
                <SafeIcon icon={success ? FiCheckCircle : FiMail} className="text-3xl text-cyan-400" />
              </div>
            </div>
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
            {success ? t('auth.forgotPassword.successTitle') : t('auth.forgotPassword.title')}
          </h1>
          <p className="text-sm text-gray-400">
            {success
              ? t('auth.forgotPassword.successMessage')
              : t('auth.forgotPassword.subtitle')}
          </p>
        </div>

        {success ? (
          <div className="flex flex-col items-center space-y-4 py-4">
            <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
              <SafeIcon icon={FiCheckCircle} className="w-12 h-12 text-green-400" />
            </div>
            <p className="text-center text-green-400 font-medium">
              {t('auth.forgotPassword.checkEmail')}
            </p>
            <p className="text-center text-sm text-gray-400">
              Email sent to: <span className="font-semibold text-cyan-400">{email}</span>
            </p>
            <p className="text-center text-xs text-gray-500 mt-2">
              Check your spam folder too. The link expires in 1 hour.
            </p>
            <Link
              to="/login"
              className="mt-4 flex items-center space-x-2 font-semibold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent hover:from-purple-300 hover:to-cyan-300 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4 text-purple-400" />
              <span>{t('auth.forgotPassword.backToLogin')}</span>
            </Link>
          </div>
        ) : (
          <>
            {/* Error Message */}
            {error && (
              <div className="flex items-center p-3 space-x-2 text-sm text-red-200 bg-red-500/20 rounded-lg border border-red-500/30">
                <SafeIcon icon={FiAlertCircle} className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-white mb-2">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  className="w-full py-3 px-4 text-white bg-white/5 border border-white/10 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all placeholder:text-gray-500"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 px-4 text-base font-bold text-cyan-400 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 rounded-xl hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200 shadow-lg shadow-purple-500/20"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {t('auth.forgotPassword.sending')}
                  </span>
                ) : (
                  t('auth.forgotPassword.sendLink')
                )}
              </button>
            </form>

            {/* Back to Login */}
            <p className="text-sm text-center text-gray-300">
              Remember your password?{' '}
              <Link 
                to="/login" 
                className="font-semibold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent hover:from-purple-300 hover:to-cyan-300"
              >
                Back to Login
              </Link>
            </p>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

export default ForgotPassword;
